import React, { useState, useEffect } from 'react';
import {
  TextField,
  FormControl,
  FormControlLabel,
  Checkbox,
  Grid,
  Typography,
  Paper,
  Box,
  IconButton,
  Tooltip,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import ReferenceAutocomplete from './ReferenceAutocomplete';
import { orderedPropertyEntries } from '../utils/registrySchemaUtils';

const RegistryForm = ({ schema, data, onChange, readOnly = false, lockedFields = [] }) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (data) {
      setFormData(data);
    }
  }, [data]);

  const handleChange = (fieldName, value) => {
    const newData = { ...formData, [fieldName]: value };
    setFormData(newData);
    validateField(fieldName, value);
    if (onChange) {
      onChange(newData);
    }
  };

  const validateField = (fieldName, value) => {
    const field = schema.properties[fieldName];
    if (!field) return;
    const newErrors = { ...errors };
    const isEmpty = value === undefined || value === null || value === '';

    if (field.required && isEmpty) {
      newErrors[fieldName] = 'Campo obbligatorio';
    } else if (typeof value === 'string' && field.maxLength && value.length > field.maxLength) {
      newErrors[fieldName] = `Lunghezza massima: ${field.maxLength} caratteri`;
    } else if (typeof value === 'string' && !isEmpty && field.pattern && !new RegExp(field.pattern).test(value)) {
      newErrors[fieldName] = 'Formato non valido';
    } else {
      delete newErrors[fieldName];
    }
    setErrors(newErrors);
  };

  const renderField = (fieldName, field) => {
    const value = formData[fieldName] || '';
    const error = errors[fieldName];
    const locked = lockedFields.includes(fieldName);

    // Reference fields: autocomplete over another registry's entries
    if (field.type === 'reference') {
      return (
        <ReferenceAutocomplete
          key={fieldName}
          field={field}
          value={formData[fieldName] ?? ''}
          onChange={(newValue) => handleChange(fieldName, newValue)}
          readOnly={readOnly || locked}
          error={error}
        />
      );
    }

    if (field.enum && Array.isArray(field.enum)) {
      return (
        <FormControl fullWidth error={!!errors[fieldName]} key={fieldName}>
          <InputLabel id={`${fieldName}-label`}>{field.name || fieldName}</InputLabel>
          <Select
            labelId={`${fieldName}-label`}
            label={field.name || fieldName}
            value={formData[fieldName] ?? ''}
            onChange={(e) => handleChange(fieldName, e.target.value)}
            disabled={readOnly || locked}
          >
            <MenuItem value=""><em>—</em></MenuItem>
            {field.enum.map((opt) => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    // Handle regular fields
    switch (field.type) {
      case 'string':
        return (
          <TextField
            key={fieldName}
            fullWidth
            label={field.name}
            value={value}
            onChange={(e) => handleChange(fieldName, e.target.value)}
            disabled={readOnly || locked}
            error={!!error}
            helperText={error || (locked ? 'La chiave non è modificabile' : field.description)}
            inputProps={{ maxLength: field.maxLength }}
          />
        );
      case 'text':
        return (
          <TextField
            key={fieldName}
            fullWidth
            multiline
            minRows={3}
            label={field.name}
            value={value}
            onChange={(e) => handleChange(fieldName, e.target.value)}
            disabled={readOnly || locked}
            error={!!error}
            helperText={error || (locked ? 'La chiave non è modificabile' : field.description)}
            inputProps={{ maxLength: field.maxLength }}
          />
        );
      case 'number':
        return (
          <TextField
            key={fieldName}
            fullWidth
            type="number"
            label={field.name}
            value={value}
            onChange={(e) => handleChange(fieldName, e.target.value)}
            disabled={readOnly || locked}
            error={!!error}
            helperText={error || (locked ? 'La chiave non è modificabile' : field.description)}
          />
        );
      case 'boolean':
        return (
          <FormControlLabel
            key={fieldName}
            control={
              <Checkbox
                checked={!!value}
                disabled={readOnly || locked}
                onChange={(e) => handleChange(fieldName, e.target.checked)}
              />
            }
            label={field.name}
          />
        );
      case 'date':
        return (
          <TextField
            key={fieldName}
            fullWidth
            type="date"
            label={field.name}
            value={value}
            onChange={(e) => handleChange(fieldName, e.target.value)}
            disabled={readOnly || locked}
            error={!!error}
            helperText={error || (locked ? 'La chiave non è modificabile' : field.description)}
            InputLabelProps={{ shrink: true }}
          />
        );
      case 'datetime':
        return (
          <TextField
            key={fieldName}
            fullWidth
            type="datetime-local"
            label={field.name}
            value={value}
            onChange={(e) => handleChange(fieldName, e.target.value)}
            disabled={readOnly || locked}
            error={!!error}
            helperText={error || (locked ? 'La chiave non è modificabile' : field.description)}
            InputLabelProps={{ shrink: true }}
          />
        );
      case 'time':
        return (
          <TextField
            key={fieldName}
            fullWidth
            type="time"
            label={field.name}
            value={value}
            onChange={(e) => handleChange(fieldName, e.target.value)}
            disabled={readOnly || locked}
            error={!!error}
            helperText={error || (locked ? 'La chiave non è modificabile' : field.description)}
            InputLabelProps={{ shrink: true }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        {schema.title || 'Dati Anagrafica'}
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        {schema.description}
      </Typography>
      <Grid container spacing={2}>
        {orderedPropertyEntries(schema).map(([fieldName, field]) => (
          <Grid item xs={12} sm={6} key={fieldName}>
            {renderField(fieldName, field)}
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
};

export default RegistryForm; 